import { z } from 'zod';
import { apiGet, apiPost } from '@/lib/api';

/**
 * KOD OPERACJI — DOWOLNY NAPIS Z SPS, nie zamknięta lista.
 *
 * ⚠️ STAŁA TU `z.enum([...])` I TO BYŁO ODBICIE BŁĘDU PO STRONIE BAZY. Portal odrzucał przy
 * wczytywaniu każdy wiersz `TransactionLog` o kodzie spoza czwórki, więc enum na froncie nigdy
 * nie miał okazji się wywalić. Odkąd wydania przenosimy wiernie (słownik SPS ma na
 * `metpol_master` **49 kodów**), zamknięta lista tutaj zamieniłaby cichą utratę wiersza w bazie
 * na wywalony ekran — bo `z.enum` odrzuciłby całą stronę listy przy pierwszym `MOV` czy `LR`.
 */
export const TransCodeSchema = z.string();
export type TransCode = string;

/**
 * Kody, dla których mamy polską nazwę i kolor. Reszta pokazuje się jako SUROWY KOD.
 *
 * ⚠️ TO JEST LISTA ETYKIET, NIE LISTA DOPUSZCZALNYCH WARTOŚCI. Nigdy nie używać jej do filtrowania
 * danych wejściowych — od tego zaczęła się utrata wierszy po stronie portalu.
 */
export const KODY_Z_NAZWA = ['WN', 'REN', 'IA'] as const;

export const TransactionRowSchema = z.object({
  id: z.string(),
  /**
   * ⚠️ NAPIS, nie liczba — `decimal(27,15)` w SPS. `z.number()` przepuściłby wartość przez `Number`
   * i zgubił cyfry rozróżniające wiersze (`40693.268742756062847` → `40693.26874275607`).
   */
  tlid: z.string(),
  /** BigInt z bazy — na API zawsze string. */
  localTlid: z.string(),
  transCode: TransCodeSchema,
  qty: z.number(),
  unitCost: z.number(),
  value: z.number(),
  ts: z.string(),
  machine: z.object({ id: z.string(), vmid: z.number(), name: z.string() }),
  // ITEMNUMBER jest TEKSTEM (np. REKAW-NITRYL-M) — kolumny ITEMID w SPS nie ma
  item: z
    .object({
      id: z.string(),
      itemNumber: z.string(),
      name: z.string(),
      unit: z.string(),
      /**
       * SPS `Items.SUPPLIERPARTNUMBER` — „numer części dostawcy".
       *
       * ⚠️ `nullable().optional()`, bo instancja starsza od tej zmiany tego pola nie odda —
       * a walidacja Zod odrzuciłaby wtedy CAŁĄ listę wydań i ekran zostałby na „Ładowanie…"
       * bez błędu w konsoli. Ta sama pułapka co przy `kind` zadań maszynowych.
       */
      supplierPartNumber: z.string().nullable().optional(),
    })
    .nullable(),
  employee: z.object({ id: z.string(), name: z.string() }).nullable(),
  /**
   * Grupy pracownika ZAMROŻONE w chwili wydania (SPS `USERGROUP01..10`).
   *
   * ⚠️ Pusta lista znaczy „NIE WIEMY" albo „poza grupami" — te przypadki są nieodróżnialne,
   * bo starsza synchronizacja tych kolumn nie przenosiła. Dlatego UI pokazuje wtedy „—",
   * a nie „bez grupy".
   */
  groupsAtIssue: z.array(z.string()),
  /**
   * Centrum kosztowe ZAMROZONE na wydaniu - napis dokladnie taki, jak zapisal go automat.
   *
   * Nie zwijamy wariantow zapisu: jesli u klienta ta sama nazwa figuruje na dwa sposoby, widac
   * dwie pozycje. Raport pokazuje konfiguracje maszyny, a nie nasze o niej wyobrazenie.
   */
  costCenterAtIssue: z.string().nullable(),
  /**
   * Numer karty Z CHWILI WYDANIA. Zamrozony celowo: zmiana numeru w SPS to DELETE+INSERT,
   * a `TransactionLog` zostaje pod starym numerem — biezacy numer pracownika odpowiadalby
   * na inne pytanie niz "na ktora karte to wydano".
   */
  userNumberAtIssue: z.string().nullable(),
});

export type TransactionRow = z.infer<typeof TransactionRowSchema>;

const SummarySchema = z.object({
  byCode: z.record(z.string(), z.object({ count: z.number(), qty: z.number(), value: z.number() })),
  totalValue: z.number(),
  issuedQty: z.number(),
  issuedValue: z.number(),
});

export type TransactionsSummary = z.infer<typeof SummarySchema>;

const TransactionsPageSchema = z.object({
  rows: z.array(TransactionRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  summary: SummarySchema,
  /**
   * Najnowsze wydanie pasujące do filtrów PO ZDJĘCIU okna czasu — wypełnione tylko wtedy,
   * gdy wynik jest pusty. Odpowiada na pytanie „nie ma nic, czy nie ma nic W TYM OKRESIE".
   */
  newestOutsideRange: z.string().nullable().optional(),
});

export type ListTransactionsParams = {
  groupId: string;
  machineId?: string;
  transCode?: TransCode;
  /**
   * Grupa pracownicza — filtr po grupach ZAMROŻONYCH na wydaniu, nie po bieżącym członkostwie.
   * Dzięki temu „ile pobrała grupa X w zamkniętym miesiącu" daje tę samą odpowiedź także
   * po przeniesieniu ludzi między grupami.
   */
  employeeGroupId?: string;
  /**
   * KONKRETNA OSOBA — filtr, o który prosi raport „ile pobrał X w okresie".
   *
   * ⚠️ TEGO POLA TU NIE BYŁO I TO JEST ZMIERZONY BŁĄD, nie uzupełnienie. Ekran raportu podawał
   * `employeeId` w obiekcie parametrów, API je obsługuje (`transactions.dto.ts`), a `tsc` milczał:
   * pole wchodziło przez rozwinięcie obiektu, więc kontrola nadmiarowych właściwości się nie
   * uruchamia. Wypadało dopiero przy składaniu adresu — poniżej nie było dla niego linii.
   * Skutek: wybór osoby nie zmieniał NICZEGO, a raport pokazywał wszystkich i wyglądał poprawnie.
   */
  employeeId?: string;
  /** Doslowna wartosc centrum; pusty napis to pelnoprawny wybor - bez centrum. */
  costCenter?: string;
  from?: string;
  to?: string;
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

export const listTransactions = (p: ListTransactionsParams) => {
  const q = new URLSearchParams({
    groupId: p.groupId,
    page: String(p.page),
    pageSize: String(p.pageSize),
    sort: p.sort,
    dir: p.dir,
  });
  if (p.machineId) q.set('machineId', p.machineId);
  if (p.transCode) q.set('transCode', p.transCode);
  if (p.employeeGroupId) q.set('employeeGroupId', p.employeeGroupId);
  if (p.employeeId) q.set('employeeId', p.employeeId);
  /** Sprawdzamy `!== undefined`, a nie prawdziwosc: pusty napis ZNACZY bez centrum i musi przejsc. */
  if (p.costCenter !== undefined) q.set('costCenter', p.costCenter);
  if (p.from) q.set('from', p.from);
  if (p.to) q.set('to', p.to);
  if (p.search) q.set('search', p.search);
  return apiGet(`/transactions?${q.toString()}`, TransactionsPageSchema);
};

/** Wartosci centrow kosztowych obecne w wydaniach - doslowne, bez zwijania wariantow. */
export const CentraKosztoweSchema = z.array(z.object({ wartosc: z.string(), wydan: z.number() }));
export type CentrumKosztoweWydan = z.infer<typeof CentraKosztoweSchema>[number];

export const fetchCentraKosztowe = (groupId: string) =>
  apiGet(`/transactions/centra-kosztowe?groupId=${encodeURIComponent(groupId)}`, CentraKosztoweSchema);

/**
 * KOREKTA CENY W HISTORII WYDAŃ.
 *
 * ⚠️ DWIE TRASY, NIE JEDNA Z PRZEŁĄCZNIKIEM. Podgląd i zatwierdzenie to osobne adresy, bo przy
 * operacji, której SPS nie cofnie, różnica między „pokaż" a „zrób" ma być widoczna w adresie,
 * a nie w polu ciała żądania.
 */
export const PodgladKorektySchema = z.object({
  towar: z.object({ itemNumber: z.string(), name: z.string(), cenaBiezaca: z.number() }),
  /** Wiersze, które REALNIE się zmienią — bez tych, które już mają docelową cenę. */
  doZmiany: z.number(),
  juzPoprawne: z.number(),
  wgKodu: z.array(z.object({ kod: z.string(), wierszy: z.number() })),
  ceny: z.array(z.object({ cena: z.number(), wierszy: z.number() })),
  wartoscPrzed: z.number(),
  wartoscPo: z.number(),
});
export type PodgladKorekty = z.infer<typeof PodgladKorektySchema>;

export type ZadanieKorekty = {
  groupId: string;
  itemId: string;
  machineId?: string;
  od: string;
  do: string;
  cena: number;
  /** Pominięte = wszystkie typy operacji. Pusta lista to odmowa po stronie serwera. */
  kody?: string[];
  zmienCeneBiezaca: boolean;
};

export const podgladKorektyCeny = (z_: ZadanieKorekty) =>
  apiPost('/transactions/korekta-ceny/podglad', z_, PodgladKorektySchema);

export const wykonajKorekteCeny = (z_: ZadanieKorekty) =>
  apiPost(
    '/transactions/korekta-ceny',
    z_,
    z.object({
      opId: z.string(),
      poprawionych: z.number(),
      pominietych: z.number(),
      wartoscPrzed: z.number(),
      wartoscPo: z.number(),
    }),
  );
